import { MaintenanceIcon } from '@patternfly/react-icons/dist/esm/icons/maintenance-icon';
import * as _ from 'lodash';
import '@console/internal/i18n.js';
import { MachineModel, NodeModel, CertificateSigningRequestModel } from '@console/internal/models';
import { referenceForModel } from '@console/internal/module/k8s';
import {
  DashboardsOverviewInventoryItem,
  Plugin,
  ResourceListPage,
  ResourceDetailsPage,
  RoutePage,
  ModelFeatureFlag,
  ModelDefinition,
  DashboardsOverviewResourceActivity,
  DashboardsOverviewInventoryItemReplacement,
  DashboardsInventoryItemGroup,
  CustomFeatureFlag,
  HorizontalNavTab,
} from '@console/plugin-sdk';
// TODO(jtomasek): change this to '@console/shared/src/utils' once @console/shared/src/utils modules
// no longer import from @console/internal (cyclic deps issues)
import { HOST_POWER_STATUS_POWERING_OFF, HOST_POWER_STATUS_POWERING_ON } from './constants';
import {
  detectBaremetalPlatform,
  BAREMETAL_FLAG,
  NODE_MAINTENANCE_FLAG,
  detectBMOEnabled,
  NODE_MAINTENANCE_KV_BETA_FLAG,
  NODE_MAINTENANCE_KV_ALPHA_FLAG,
} from './features';
import {
  BareMetalHostModel,
  NodeMaintenanceModel,
  NodeMaintenanceKubevirtAlphaModel,
  NodeMaintenanceKubevirtBetaModel,
} from './models';
import { getHostPowerStatus, hasPowerManagement, isDetached } from './selectors';
import { BareMetalHostKind } from './types';

type ConsumedExtensions =
  | DashboardsOverviewInventoryItem
  | DashboardsOverviewInventoryItemReplacement
  | DashboardsInventoryItemGroup
  | ResourceListPage
  | ResourceDetailsPage
  | RoutePage
  | ModelFeatureFlag
  | ModelDefinition
  | CustomFeatureFlag
  | DashboardsOverviewResourceActivity
  | HorizontalNavTab;

const METAL3_FLAG = 'METAL3';

const plugin: Plugin<ConsumedExtensions> = [
  {
    type: 'ModelDefinition',
    properties: {
      models: [
        BareMetalHostModel,
        NodeMaintenanceModel,
        NodeMaintenanceKubevirtAlphaModel,
        NodeMaintenanceKubevirtBetaModel,
      ],
    },
  },
  {
    type: 'FeatureFlag/Model',
    properties: {
      model: BareMetalHostModel,
      flag: METAL3_FLAG,
    },
  },
  {
    type: 'FeatureFlag/Model',
    properties: {
      model: NodeMaintenanceModel,
      flag: NODE_MAINTENANCE_FLAG,
    },
  },
  {
    type: 'FeatureFlag/Model',
    properties: {
      model: NodeMaintenanceKubevirtBetaModel,
      flag: NODE_MAINTENANCE_KV_BETA_FLAG,
    },
  },
  {
    type: 'FeatureFlag/Model',
    properties: {
      model: NodeMaintenanceKubevirtAlphaModel,
      flag: NODE_MAINTENANCE_KV_ALPHA_FLAG,
    },
  },
  {
    type: 'FeatureFlag/Custom',
    properties: {
      detect: detectBaremetalPlatform,
    },
  },
  {
    type: 'FeatureFlag/Custom',
    properties: {
      detect: detectBMOEnabled,
    },
  },
  {
    type: 'Page/Resource/List',
    properties: {
      model: BareMetalHostModel,
      loader: () =>
        import(
          './components/baremetal-hosts/BareMetalHostsPage' /* webpackChunkName: "metal3-baremetalhost" */
        ).then((m) => m.default),
    },
  },
  {
    type: 'Page/Resource/Details',
    properties: {
      model: BareMetalHostModel,
      loader: () =>
        import(
          './components/baremetal-hosts/BareMetalHostDetailsPage' /* webpackChunkName: "metal3-baremetalhost" */
        ).then((m) => m.default),
    },
  },
  {
    type: 'Page/Route',
    properties: {
      exact: true,
      path: `/k8s/ns/:ns/${referenceForModel(BareMetalHostModel)}/~new/form`,
      loader: () =>
        import(
          './components/baremetal-hosts/add-baremetal-host/AddBareMetalHostPage' /* webpackChunkName: "metal3-baremetalhost" */
        ).then((m) => m.default),
    },
    flags: {
      required: [BAREMETAL_FLAG, METAL3_FLAG],
    },
  },
  {
    type: 'Page/Route',
    properties: {
      exact: true,
      path: `/k8s/ns/:ns/${referenceForModel(BareMetalHostModel)}/:name/edit`,
      loader: () =>
        import(
          './components/baremetal-hosts/add-baremetal-host/AddBareMetalHostPage' /* webpackChunkName: "metal3-baremetalhost" */
        ).then((m) => m.default),
    },
    flags: {
      required: [BAREMETAL_FLAG, METAL3_FLAG],
    },
  },
  {
    type: 'Dashboards/Overview/Inventory/Item/Replacement',
    properties: {
      model: NodeModel,
      additionalResources: {
        kvAlphaMaintenances: {
          isList: true,
          kind: referenceForModel(NodeMaintenanceKubevirtAlphaModel),
          optional: true,
        },
        kvBetaMaintenances: {
          isList: true,
          kind: referenceForModel(NodeMaintenanceKubevirtBetaModel),
          optional: true,
        },
        maintenances: {
          isList: true,
          kind: referenceForModel(NodeMaintenanceModel),
          optional: true,
        },
        csrs: {
          isList: true,
          kind: CertificateSigningRequestModel.kind,
          optional: true,
        },
      },
      mapper: () =>
        import('./components/baremetal-nodes/dashboard/utils' /* webpackChunkName: "node" */).then(
          (m) => m.getBMNStatusGroups,
        ),
    },
    flags: {
      required: [BAREMETAL_FLAG, METAL3_FLAG],
    },
  },
  {
    type: 'Dashboards/Overview/Inventory/Item',
    properties: {
      additionalResources: {
        machines: {
          isList: true,
          kind: referenceForModel(MachineModel),
        },
        nodes: {
          isList: true,
          kind: NodeModel.kind,
        },
        kvAlphaMaintenances: {
          isList: true,
          kind: referenceForModel(NodeMaintenanceKubevirtAlphaModel),
          optional: true,
        },
        kvBetaMaintenances: {
          isList: true,
          kind: referenceForModel(NodeMaintenanceKubevirtBetaModel),
          optional: true,
        },
        maintenances: {
          isList: true,
          kind: referenceForModel(NodeMaintenanceModel),
          optional: true,
        },
      },
      model: BareMetalHostModel,
      mapper: () =>
        import(
          './components/baremetal-hosts/dashboard/utils' /* webpackChunkName: "metal3-baremetalhost" */
        ).then((m) => m.getBMHStatusGroups),
    },
    flags: {
      required: [BAREMETAL_FLAG, METAL3_FLAG],
    },
  },
  {
    type: 'Dashboards/Overview/Activity/Resource',
    properties: {
      k8sResource: {
        isList: true,
        kind: referenceForModel(NodeMaintenanceModel),
        prop: 'maintenances',
      },
      isActivity: (resource) => _.get(resource.status, 'phase') === 'Running',
      getTimestamp: (resource) => new Date(resource.metadata.creationTimestamp),
      loader: () =>
        import(
          './components/maintenance/MaintenanceDashboardActivity' /* webpackChunkName: "node-maintenance" */
        ).then((m) => m.default),
    },
    flags: {
      required: [BAREMETAL_FLAG, METAL3_FLAG, NODE_MAINTENANCE_FLAG],
    },
  },
  {
    type: 'Dashboards/Overview/Activity/Resource',
    properties: {
      k8sResource: {
        isList: true,
        kind: referenceForModel(NodeMaintenanceKubevirtAlphaModel),
        prop: 'maintenances',
      },
      isActivity: (resource) => _.get(resource.status, 'phase') === 'Running',
      getTimestamp: (resource) => new Date(resource.metadata.creationTimestamp),
      loader: () =>
        import(
          './components/maintenance/MaintenanceDashboardActivity' /* webpackChunkName: "node-maintenance" */
        ).then((m) => m.default),
    },
    flags: {
      required: [BAREMETAL_FLAG, METAL3_FLAG, NODE_MAINTENANCE_KV_ALPHA_FLAG],
    },
  },
  {
    type: 'Dashboards/Overview/Activity/Resource',
    properties: {
      k8sResource: {
        isList: true,
        kind: referenceForModel(NodeMaintenanceKubevirtBetaModel),
        prop: 'maintenances',
      },
      isActivity: (resource) => _.get(resource.status, 'phase') === 'Running',
      getTimestamp: (resource) => new Date(resource.metadata.creationTimestamp),
      loader: () =>
        import(
          './components/maintenance/MaintenanceDashboardActivity' /* webpackChunkName: "node-maintenance" */
        ).then((m) => m.default),
    },
    flags: {
      required: [BAREMETAL_FLAG, METAL3_FLAG, NODE_MAINTENANCE_KV_BETA_FLAG],
    },
  },
  {
    type: 'Dashboards/Inventory/Item/Group',
    properties: {
      id: 'node-maintenance',
      icon: <MaintenanceIcon />,
    },
  },
  {
    type: 'Dashboards/Overview/Activity/Resource',
    properties: {
      k8sResource: {
        kind: referenceForModel(BareMetalHostModel),
        prop: 'bmhs',
        isList: true,
      },
      isActivity: (resource: BareMetalHostKind) =>
        [HOST_POWER_STATUS_POWERING_OFF, HOST_POWER_STATUS_POWERING_ON].includes(
          getHostPowerStatus(resource),
        ) &&
        hasPowerManagement(resource) &&
        !isDetached(resource),
      loader: () =>
        import(
          './components/baremetal-hosts/dashboard/BareMetalStatusActivity' /* webpackChunkName: "metal3-powering" */
        ).then((m) => m.default),
    },
    flags: {
      required: [BAREMETAL_FLAG, METAL3_FLAG],
    },
  },
];

export default plugin;
